import { PlusIcon } from '@heroicons/react/24/outline';
import { intlFormat, isFuture, isSameDay } from 'date-fns';
import { AutoSaveIndicator } from '../../../components/common/auto-save-indicator.tsx';
import { LoadingBlockFullPage } from '../../../components/loading/loading-block-full-page.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { DrawerContainer, DrawerContent, DrawerHeader } from '../../../components/ui/drawer.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { getEventMetadata, hasOccurrenceOn } from '../../../utils/event-utils.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { CommentsThread } from '../../comments/components/comments-thread.tsx';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';
import { useUpdateNote } from '../../notes/hooks/use-update-note.ts';
import { TraskCreateDialog } from '../../tasks/components/task-create-dialog.tsx';
import { EventDetailsSchedule } from './event-details-schedule.tsx';
import { EventEditDialog } from './event-edit-dialog.tsx';
import { EventNoteCreate } from './event-note-create.tsx';
import { EventNoteForm } from './event-note-form.tsx';
import { EventOptionsDropdown } from './event-options-dropdown.tsx';

export type EventDetailsProps = {
    date: string;
    eventId: string;
    className?: string;
    onBack?: () => void;
    onClose?: () => void;
    onUpdated?: () => void;
};

export const EventDetails = ({ date, eventId, className, onBack, onClose, onUpdated }: EventDetailsProps) => {
    const { t, locale } = useLocale('events');
    const sendEvent = useAnalytics();
    const { userId } = useAppConfig();

    // Use the direct getEventById endpoint
    const { data: eventDetails, isLoading } = api.platoUser.query.events.getEventById.useQuery(
        {
            eventId,
        },
        {
            enabled: !!eventId,
        },
    );

    const updateNoteMutation = useUpdateNote();

    const handleEdit = () => {
        if (!eventDetails) {
            return;
        }
        DialogStateManager.show(EventEditDialog, {
            eventId,
            eventDetails,
            onUpdated,
        });
    };

    if (isLoading || !eventDetails) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    // Determine if the current user is the owner of the event
    const isOwner = eventDetails.owner_id === userId;

    const occurenceDate = new Date(date);
    const isOccurrence = hasOccurrenceOn(eventDetails, date);
    const noteDetails = eventDetails.notes.find((note) => isSameDay(note.occurredAt, occurenceDate));

    return (
        <DrawerContainer className={className}>
            <DrawerHeader
                onBack={onBack}
                onClose={onClose}
                actions={
                    eventDetails &&
                    isOwner && (
                        <>
                            <Button
                                variant="subtle"
                                icon={PlusIcon}
                                onClick={() => {
                                    void DialogStateManager.show(TraskCreateDialog, {
                                        links: eventDetails.customer?.customerId
                                            ? [
                                                  {
                                                      modelType: SearchEntity.Customer,
                                                      modelId: eventDetails.customer.customerId,
                                                      title: eventDetails.customer.customerName ?? null,
                                                  },
                                              ]
                                            : undefined,
                                        source: 'event_details',
                                    });
                                }}
                            >
                                {t('details__new_task', { ns: 'events' })}
                            </Button>
                            <EventOptionsDropdown
                                date={date}
                                event={eventDetails}
                                onEdit={handleEdit}
                                onDelete={() => {
                                    onUpdated?.();
                                    onClose?.();
                                    sendEvent('event_deleted', getEventMetadata(eventDetails));
                                }}
                            />
                        </>
                    )
                }
                title={t('details__title')}
            >
                <AutoSaveIndicator
                    isUpdating={updateNoteMutation.isLoading}
                    isSuccess={updateNoteMutation.isSuccess}
                    isError={updateNoteMutation.isError}
                />
            </DrawerHeader>
            <DrawerContent>
                <EventDetailsSchedule eventDetails={eventDetails} />

                {/* If we don't have a customer, this section should be hidden */}
                {eventDetails.customerId && isOccurrence && (noteDetails ?? !isFuture(occurenceDate)) && (
                    <div className="flex flex-col gap-3">
                        <Text color="neutral-950" weight="medium">
                            {t('note_for_date', {
                                date: intlFormat(
                                    occurenceDate,
                                    {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric',
                                    },
                                    { locale },
                                ),
                            })}
                        </Text>

                        {noteDetails ? (
                            <div className="flex flex-col gap-3">
                                <EventNoteForm
                                    note={noteDetails}
                                    onUpdate={(values) => {
                                        updateNoteMutation.mutate({
                                            noteId: noteDetails.noteId,
                                            title: values.title,
                                            attachments: values.attachments,
                                        });
                                    }}
                                />

                                <CommentsThread noteId={noteDetails.noteId} />
                            </div>
                        ) : (
                            eventDetails.customerId && (
                                <EventNoteCreate
                                    event={{ ...eventDetails, customerId: eventDetails.customerId }}
                                    occurrenceDate={date}
                                />
                            )
                        )}
                    </div>
                )}
            </DrawerContent>
        </DrawerContainer>
    );
};
